import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, X, ArrowRight, Smartphone, Zap, TrendingUp, Users, CreditCard, BarChart3, Sparkles, ChefHat, Wallet } from 'lucide-react'
import Logo from '../components/Logo'

export default function LandingPage() {
  const [scrollY, setScrollY] = useState(0)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Navigation - Glassmorphism */}
      <nav className="fixed top-0 w-full bg-black/60 backdrop-blur-xl border-b border-white/10 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-2">
              <Logo size={36} variant="icon" />
              <span className="text-xl font-semibold tracking-tight">Qorder</span>
            </div>
            <div className="flex items-center gap-6">
              <Link
                to="/login"
                className="px-5 py-2.5 text-sm font-medium text-white/80 hover:text-white transition-colors"
              >
                Accedi
              </Link>
              <Link
                to="/signup"
                className="px-6 py-2.5 text-sm font-semibold bg-white text-black rounded-full hover:bg-white/90 transition-all shadow-lg shadow-white/20 hover:shadow-xl hover:shadow-white/30 hover:scale-105"
              >
                Inizia Gratis
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Cinematic */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-black">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(17,24,39,0),rgba(0,0,0,1))]"></div>
          <div
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-[120px] animate-pulse"
            style={{ transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.15}px)` }}
          ></div>
          <div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-[120px] animate-pulse delay-1000"
            style={{ transform: `translate(-${scrollY * 0.1}px, -${scrollY * 0.15}px)` }}
          ></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-20">
          {/* Floating Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full text-sm font-medium mb-8 hover:bg-white/10 transition-all group">
            <Sparkles className="w-4 h-4 text-blue-400 group-hover:rotate-12 transition-transform" />
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Gratis con solo 2% per transazione
            </span>
          </div>

          {/* Main Headline - Apple Style */}
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-6 leading-[1.1] tracking-tight">
            Il futuro del
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient">
              ristorante
            </span>
            <br />
            è digitale.
          </h1>

          <p className="text-xl md:text-2xl text-white/60 mb-12 max-w-3xl mx-auto leading-relaxed font-light">
            Ordini via QR code. Pagamenti istantanei. Gestione completa.
            <br />
            <strong className="text-white/90 font-semibold">+30% di ordini</strong> senza alcun sforzo.
          </p>

          {/* CTA Buttons - Apple Style */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/signup"
              className="group px-10 py-5 text-lg font-semibold bg-white text-black rounded-full hover:bg-white/90 transition-all shadow-2xl shadow-white/20 hover:shadow-white/40 hover:scale-105 flex items-center justify-center gap-2"
            >
              Inizia Gratis
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#demo"
              className="px-10 py-5 text-lg font-semibold border-2 border-white/20 text-white rounded-full hover:bg-white/5 hover:border-white/40 transition-all backdrop-blur-xl flex items-center justify-center gap-2"
            >
              Guarda il Demo
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-8 text-sm text-white/40">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              Setup in 5 minuti
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              Nessuna carta richiesta
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              Cancella quando vuoi
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/20 rounded-full p-1">
            <div className="w-1 h-3 bg-white/60 rounded-full mx-auto animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Stats Section - Glassmorphism Cards */}
      <section className="relative py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <StatCard number="1,000+" label="Ristoranti" />
            <StatCard number="€4.3M" label="Transazioni" />
            <StatCard number="+30%" label="Ordini in Più" />
            <StatCard number="4.9★" label="Rating" />
          </div>
        </div>
      </section>

      {/* Features - Bento Grid Style */}
      <section className="relative py-32 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
              Potente.
              <br />
              Semplice.
              <br />
              <span className="text-white/40">Tutto in uno.</span>
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <BentoCard
              icon={<Smartphone className="w-8 h-8" />}
              title="QR Ordering"
              description="I clienti scansionano e ordinano. Nessuna app, zero attrito."
              gradient="from-blue-500/20 to-cyan-500/20"
              className="lg:col-span-2"
            />
            <BentoCard
              icon={<CreditCard className="w-8 h-8" />}
              title="Pagamenti"
              description="Stripe, Satispay, split bill automatico."
              gradient="from-purple-500/20 to-pink-500/20"
            />
            <BentoCard
              icon={<BarChart3 className="w-8 h-8" />}
              title="Analytics"
              description="Insights in tempo reale. Decisioni data-driven."
              gradient="from-orange-500/20 to-red-500/20"
            />
            <BentoCard
              icon={<ChefHat className="w-8 h-8" />}
              title="Menu Engineering"
              description="A/B testing, suggerimenti AI, ottimizzazione prezzi."
              gradient="from-green-500/20 to-emerald-500/20"
              className="lg:col-span-2"
            />
            <BentoCard
              icon={<Users className="w-8 h-8" />}
              title="Multi-Location"
              description="Dashboard unificata per tutte le tue sedi."
              gradient="from-indigo-500/20 to-blue-500/20"
            />
          </div>
        </div>
      </section>

      {/* Competitor Comparison - Minimalist Table */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-black via-gray-900/50 to-black">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
              Perché Qorder?
            </h2>
            <p className="text-xl text-white/60 font-light">
              Più feature. Stesso prezzo. Nessun compromesso.
            </p>
          </div>

          {/* Comparison Table - Apple Style */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-6 px-8 font-semibold text-white/60 text-sm">Feature</th>
                    <th className="py-6 px-6 font-bold text-lg">
                      <div className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                        Qorder
                      </div>
                    </th>
                    <th className="py-6 px-6 font-semibold text-white/40 text-sm">Sunday</th>
                    <th className="py-6 px-6 font-semibold text-white/40 text-sm">Square</th>
                    <th className="py-6 px-6 font-semibold text-white/40 text-sm">Toast</th>
                  </tr>
                </thead>
                <tbody>
                  <ComparisonRow
                    feature="Costo Setup"
                    qorder="€0"
                    others={["€0", "€0", "€1,500"]}
                    highlight
                  />
                  <ComparisonRow
                    feature="Canone Mensile"
                    qorder="€0"
                    others={["€0", "€0", "€69/m"]}
                    highlight
                  />
                  <ComparisonRow
                    feature="Fee Transazione"
                    qorder="2.0%"
                    others={["1.9%", "2.6%", "2.49%"]}
                  />
                  <ComparisonRow
                    feature="QR Ordering"
                    qorder={true}
                    others={[true, true, true]}
                  />
                  <ComparisonRow
                    feature="Analytics Avanzate"
                    qorder={true}
                    others={[false, true, true]}
                    highlight
                  />
                  <ComparisonRow
                    feature="Marketing Automation"
                    qorder={true}
                    others={[false, false, true]}
                    highlight
                  />
                  <ComparisonRow
                    feature="Gestione Inventario"
                    qorder={true}
                    others={[false, false, true]}
                    highlight
                  />
                  <ComparisonRow
                    feature="Support Italiano"
                    qorder={true}
                    others={[false, false, false]}
                    highlight
                  />
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/signup"
              className="inline-flex items-center px-10 py-5 text-lg font-semibold bg-white text-black rounded-full hover:bg-white/90 transition-all shadow-2xl shadow-white/20 hover:scale-105"
            >
              Inizia Gratis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing - Apple Style Cards */}
      <section className="relative py-32 px-6" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
              Prezzi chiari.
              <br />
              <span className="text-white/40">Nessuna sorpresa.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Free Tier */}
            <PricingCard
              name="Free"
              price="0"
              fee="2%"
              features={[
                "Menu digitale illimitato",
                "QR code tavoli",
                "1 location",
                "Analytics base",
                "Support email"
              ]}
              cta="Inizia Gratis"
              ctaLink="/signup"
            />

            {/* Starter Tier - Highlighted */}
            <PricingCard
              name="Starter"
              price="49"
              fee="2.5%"
              features={[
                "Tutto in Free",
                "Fino a 5 locations",
                "Analytics avanzate",
                "Marketing automation",
                "Support prioritario"
              ]}
              cta="Prova Gratis"
              ctaLink="/signup"
              highlighted
              badge="PIÙ POPOLARE"
            />

            {/* Pro Tier */}
            <PricingCard
              name="Pro"
              price="149"
              fee="2.8%"
              features={[
                "Tutto in Starter",
                "Locations illimitate",
                "Gestione inventario",
                "White-label",
                "Account manager dedicato"
              ]}
              cta="Contattaci"
              ctaLink="/signup"
            />
          </div>

          {/* ROI Calculator - Glassmorphism */}
          <div className="mt-20 max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 backdrop-blur-xl border border-white/10 rounded-3xl p-10">
              <div className="text-center mb-8">
                <Wallet className="w-12 h-12 mx-auto mb-4 text-blue-400" />
                <h3 className="text-3xl font-bold mb-2">Calcola il Tuo ROI</h3>
                <p className="text-white/60">Con €10,000 di transazioni mensili:</p>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center">
                  <div className="text-sm text-white/60 mb-2">Costo Qorder</div>
                  <div className="text-4xl font-bold text-blue-400">€200</div>
                  <div className="text-xs text-white/40 mt-1">al mese</div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center">
                  <div className="text-sm text-white/60 mb-2">Aumento Ordini</div>
                  <div className="text-4xl font-bold text-green-400">+€3,000</div>
                  <div className="text-xs text-white/40 mt-1">al mese (+30%)</div>
                </div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-white/80 mb-2">ROI Netto Mensile</div>
                <div className="text-5xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  +€2,800
                </div>
                <div className="text-sm text-white/60 mt-4">
                  Qorder si ripaga da solo e genera profitto extra
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Minimal Cards */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-black via-gray-900/30 to-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold tracking-tight">
              Amato dai ristoratori.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <TestimonialCard
              quote="Abbiamo aumentato gli ordini del 35% in 3 mesi. I clienti adorano non dover aspettare."
              name="Marco Rossi"
              role="Trattoria da Marco"
              location="Milano"
            />
            <TestimonialCard
              quote="Setup in 10 minuti, zero complicazioni. Il sistema più semplice che abbia mai usato."
              name="Sofia Bianchi"
              role="La Pizzeria del Corso"
              location="Roma"
            />
            <TestimonialCard
              quote="Le analytics ci hanno fatto capire quali piatti funzionano. Margini su del 20%."
              name="Luca Ferrari"
              role="Osteria Moderna"
              location="Firenze"
            />
          </div>
        </div>
      </section>

      {/* FAQ - Accordion Style */}
      <section className="relative py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold tracking-tight">
              Domande?
              <br />
              <span className="text-white/40">Risposte.</span>
            </h2>
          </div>

          <div className="space-y-4">
            <FAQItem
              question="Quanto costa davvero Qorder?"
              answer="Piano FREE: €0 fissi + 2% per transazione. Piano Starter: €49/mese + 2.5%. Nessun costo nascosto, nessun vincolo."
            />
            <FAQItem
              question="I clienti devono scaricare un'app?"
              answer="No! Tutto dal browser. Scansiona QR code e ordina da Chrome/Safari. Zero download, zero attrito."
            />
            <FAQItem
              question="Quanto tempo serve per il setup?"
              answer="5-10 minuti. Registrati, carica menu, genera QR, stampa. Inizi a ricevere ordini lo stesso giorno."
            />
            <FAQItem
              question="Posso cancellare quando voglio?"
              answer="Sì, nessun vincolo. Cancelli con 1 click. I tuoi dati rimangono tuoi e puoi esportarli sempre."
            />
            <FAQItem
              question="Che metodi di pagamento accettate?"
              answer="Stripe (carte), Satispay, contanti. Split bill automatico per tavoli con più persone."
            />
          </div>
        </div>
      </section>

      {/* Final CTA - Cinematic */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-pink-600/20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),rgba(0,0,0,0.9))]"></div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight leading-tight">
            Pronto per il
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              salto digitale?
            </span>
          </h2>
          <p className="text-xl text-white/60 mb-12 max-w-2xl mx-auto font-light">
            Unisciti a 1,000+ ristoranti che hanno trasformato l'esperienza clienti.
            Setup in 5 minuti. Zero rischi.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center px-12 py-6 text-xl font-bold bg-white text-black rounded-full hover:bg-white/90 transition-all shadow-2xl shadow-white/30 hover:scale-105"
          >
            Inizia Gratis Ora
            <ArrowRight className="ml-3 w-6 h-6" />
          </Link>
          <div className="mt-8 flex items-center justify-center gap-8 text-sm text-white/40">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              Nessuna carta
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              Setup 5 minuti
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              Support italiano
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer className="relative border-t border-white/10 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Logo size={32} variant="icon" />
                <span className="text-lg font-semibold">Qorder</span>
              </div>
              <p className="text-sm text-white/40">
                La piattaforma per digitalizzare i ristoranti.
                <br />
                Made in Italy.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm text-white/60">Prodotto</h4>
              <ul className="space-y-2 text-sm text-white/40">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Roadmap</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm text-white/60">Risorse</h4>
              <ul className="space-y-2 text-sm text-white/40">
                <li><a href="#" className="hover:text-white transition-colors">Documentazione</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Guide</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm text-white/60">Company</h4>
              <ul className="space-y-2 text-sm text-white/40">
                <li><a href="#" className="hover:text-white transition-colors">Chi Siamo</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contatti</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-sm text-white/40">
            <p>© 2025 CityZ S.r.l. - Qorder. All rights reserved. Made with ❤️ in Italy.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

// Components
function StatCard({ number, label }: { number: string; label: string }) {
  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center hover:bg-white/10 transition-all group">
      <div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent group-hover:scale-110 transition-transform">
        {number}
      </div>
      <div className="text-sm text-white/60">{label}</div>
    </div>
  )
}

function BentoCard({
  icon,
  title,
  description,
  gradient,
  className = ''
}: {
  icon: React.ReactNode
  title: string
  description: string
  gradient: string
  className?: string
}) {
  return (
    <div className={`group relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all overflow-hidden ${className}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-100 transition-opacity`}></div>
      <div className="relative z-10">
        <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform">
          {icon}
        </div>
        <h3 className="text-2xl font-bold mb-3">{title}</h3>
        <p className="text-white/60 leading-relaxed">{description}</p>
      </div>
    </div>
  )
}

function ComparisonRow({
  feature,
  qorder,
  others,
  highlight = false
}: {
  feature: string
  qorder: boolean | string
  others: (boolean | string)[]
  highlight?: boolean
}) {
  const renderCell = (value: boolean | string, isQorder: boolean = false) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className={`w-5 h-5 mx-auto ${isQorder ? 'text-blue-400' : 'text-green-400/40'}`} />
      ) : (
        <X className="w-5 h-5 text-white/20 mx-auto" />
      )
    }
    return (
      <span className={isQorder ? 'text-white font-semibold' : 'text-white/40'}>
        {value}
      </span>
    )
  }

  return (
    <tr className={`border-b border-white/5 ${highlight ? 'bg-blue-500/5' : ''}`}>
      <td className="py-5 px-8 text-sm text-white/80">{feature}</td>
      <td className="py-5 px-6 text-center">{renderCell(qorder, true)}</td>
      {others.map((val, idx) => (
        <td key={idx} className="py-5 px-6 text-center text-sm">{renderCell(val)}</td>
      ))}
    </tr>
  )
}

function PricingCard({
  name,
  price,
  fee,
  features,
  cta,
  ctaLink,
  highlighted = false,
  badge
}: {
  name: string
  price: string
  fee: string
  features: string[]
  cta: string
  ctaLink: string
  highlighted?: boolean
  badge?: string
}) {
  return (
    <div className={`relative bg-white/5 backdrop-blur-xl border ${highlighted ? 'border-blue-500/50 bg-gradient-to-b from-blue-500/10 to-transparent' : 'border-white/10'} rounded-3xl p-8 hover:bg-white/10 transition-all ${highlighted ? 'scale-105' : ''}`}>
      {badge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-500 px-4 py-1.5 rounded-full text-xs font-bold">
          {badge}
        </div>
      )}
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold mb-4">{name}</h3>
        <div className="mb-2">
          <span className="text-5xl font-bold">€{price}</span>
          <span className="text-white/60">/mese</span>
        </div>
        <div className="text-sm text-white/60">+ {fee} per transazione</div>
      </div>
      <ul className="space-y-3 mb-8">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-3 text-sm">
            <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <span className="text-white/80">{feature}</span>
          </li>
        ))}
      </ul>
      <Link
        to={ctaLink}
        className={`block w-full text-center px-6 py-4 rounded-full font-semibold transition-all ${
          highlighted
            ? 'bg-white text-black hover:bg-white/90 shadow-lg shadow-white/20'
            : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
        }`}
      >
        {cta}
      </Link>
    </div>
  )
}

function TestimonialCard({
  quote,
  name,
  role,
  location
}: {
  quote: string
  name: string
  role: string
  location: string
}) {
  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all">
      <div className="mb-6">
        <p className="text-white/80 leading-relaxed italic">"{quote}"</p>
      </div>
      <div>
        <div className="font-semibold">{name}</div>
        <div className="text-sm text-white/60">{role}</div>
        <div className="text-xs text-white/40">{location}</div>
      </div>
    </div>
  )
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-8 py-6 text-left flex justify-between items-center group"
      >
        <span className="font-semibold text-lg">{question}</span>
        <ArrowRight className={`w-5 h-5 text-white/60 transition-transform ${isOpen ? 'rotate-90' : ''} group-hover:text-white`} />
      </button>
      {isOpen && (
        <div className="px-8 pb-6 text-white/70 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  )
}
